/**
 * EmployerDashboardPanel.jsx — Pass-1 Beta Activation, Slices 2 + 6.
 *
 * Compact, READ-ONLY summary surfaced on the Employer Home page,
 * above the org editor. Aggregates the operational signal the
 * admin/recruiter needs to orient themselves at a glance:
 *
 *   • active roles count        (existing /api/employer/roles)
 *   • account-wide open reviews (Supabase JS, admin RLS, additive)
 *   • active reviewers          (Supabase JS, admin RLS, additive)
 *   • pending invitations       (Supabase JS, admin RLS, additive)
 *   • plan tier + caps          (existing /api/employer/v1/plan)
 *   • activation status badge   (read-only, derived; no writes)
 *
 * Strictly:
 *   - No write paths.
 *   - No new endpoints.
 *   - No billing-state assertions.
 *   - No "active subscription" / "live plan" language.
 *   - No self-serve activation affordance.
 *   - All reads degrade gracefully on failure (counter shows "—").
 *
 * Gating: rendered by the parent only when the caller's
 *   /v1/me → member.role is `admin` or `recruiter`. Reviewer
 *   callers see the existing Reviewer Queue empty/loaded surfaces
 *   instead.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, RefreshCw, Briefcase, UserCheck, MailPlus, ClipboardList,
  ShieldCheck, Tag, AlertCircle, ArrowRight,
} from 'lucide-react';

import { apiFetch } from '@/lib/api';
import { supabase } from '@/lib/supabase';
import { planLabel, planSubtitle } from '@/lib/employerPlanLabels';
import { ROLE_LABEL, ROLE_DESCRIPTION } from '@/lib/employerAuthority';
import { isActiveRoleStatus } from '@/lib/roleLifecycle';


const PLACEHOLDER_PLAN_COPY =
  'Placeholder pricing — billing not yet active.';
const ACTIVATION_PENDING_COPY =
  'Activation pending founder review.';
const ACTIVATION_ACTIVE_COPY =
  'Activation in effect.';


function fmtCap(v) {
  if (v === null || v === undefined) return 'unlimited';
  return String(v);
}


function fmtUsage(used, cap) {
  // Returns "N / cap" where cap may be null (unlimited) or undefined
  // (entitlement row missing).
  const u = (used === null || used === undefined) ? '\u2014' : used;
  if (cap === null) return `${u} / \u221E`;
  if (cap === undefined) return `${u} / \u2014`;
  return `${u} / ${cap}`;
}


export default function EmployerDashboardPanel({ accountId, memberRole }) {
  const [loading,    setLoading]   = useState(true);
  const [counts,     setCounts]    = useState({
    activeRoles:        null,
    accountOpenReviews: null,
    activeReviewers:    null,
    pendingInvites:     null,
  });
  const [plan,       setPlan]     = useState(null);

  const load = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);

    const settled = await Promise.allSettled([
      apiFetch('/api/employer/roles'),
      apiFetch('/api/employer/v1/plan'),
      supabase
        .from('emp_candidate_reviews')
        .select('review_id, status')
        .eq('account_id', accountId)
        .in('status', ['pending', 'in_review']),
      supabase
        .from('emp_reviewer_assignments')
        .select('assignment_id, reviewer_user_id, assignment_status')
        .eq('account_id', accountId)
        .eq('assignment_status', 'active'),
      supabase
        .from('emp_member_invitations')
        .select('id, status')
        .eq('account_id', accountId)
        .eq('status', 'pending'),
    ]);

    const next = {
      activeRoles:        null,
      accountOpenReviews: null,
      activeReviewers:    null,
      pendingInvites:     null,
    };

    // Roles — active count.
    //
    // RC 1.61 BUG.A FIX (operator-approved 2026-05-27):
    //   Honours the canonical lifecycle-alias doctrine from
    //   `backend/core/emp_role_lifecycle.py:_STATUS_ALIASES`, where
    //   `'live'` IS the deprecated read-alias of `'active'`. Both
    //   values represent the SAME lifecycle state. The shared
    //   helper `isActiveRoleStatus` is the single source of truth.
    //   Adding a new alias there propagates everywhere.
    if (settled[0].status === 'fulfilled') {
      const list = settled[0].value?.items
        || settled[0].value?.roles
        || settled[0].value
        || [];
      next.activeRoles = (Array.isArray(list) ? list : []).filter(
        (r) => isActiveRoleStatus(r?.status),
      ).length;
    }

    // Plan
    if (settled[1].status === 'fulfilled') {
      setPlan(settled[1].value || null);
    }

    // Open reviews
    if (settled[2].status === 'fulfilled' && !settled[2].value?.error) {
      next.accountOpenReviews = (settled[2].value?.data || []).length;
    }

    // Active reviewers — count UNIQUE reviewer_user_id values across
    // active assignments. A reviewer can be active on multiple roles
    // but counts once.
    if (settled[3].status === 'fulfilled' && !settled[3].value?.error) {
      const rows = settled[3].value?.data || [];
      next.activeReviewers = new Set(rows.map((r) => r.reviewer_user_id)).size;
    }

    // Pending invitations
    if (settled[4].status === 'fulfilled' && !settled[4].value?.error) {
      next.pendingInvites = (settled[4].value?.data || []).length;
    }

    setCounts(next);
    setLoading(false);
  }, [accountId]);

  useEffect(() => { load(); }, [load]);

  if (!accountId) return null;
  // Reviewer-only callers see their queue instead — don't surface
  // admin counters to them.
  if (memberRole && !['owner', 'admin', 'recruiter'].includes(memberRole)) return null;

  // ── Plan + activation derivation ──────────────────────────────
  const entitlement = plan?.entitlement || null;
  const activePlan  = plan?.active_plan || null;
  const tierKey     = entitlement?.plan_tier || activePlan?.plan_tier || null;
  const tierLabel   = planLabel(tierKey);
  const tierSubtitle = planSubtitle(tierKey);
  // Phase 0.4 ships every account as "placeholder". An account is
  // considered "activated" only when the backend marks
  // active_plan.placeholder === false (Phase 0.5+).
  const isPlaceholder = entitlement?.plan_tier
    ? Boolean(activePlan?.placeholder ?? true)
    : true;
  const activationCopy = isPlaceholder
    ? ACTIVATION_PENDING_COPY
    : ACTIVATION_ACTIVE_COPY;
  const activationVariant = isPlaceholder ? 'outline' : 'default';
  const activeRolesCap  = entitlement?.active_roles_cap
                          ?? activePlan?.active_roles_cap ?? null;
  const signalsAllowance = entitlement?.signals_allowance_monthly
                          ?? activePlan?.signals_allowance_monthly ?? null;
  // ── Usage (Option I — read-only, gracefully null when entitlement absent) ──
  const activeRolesUsed = entitlement?.active_roles_current ?? null;
  const signalsUsed     = entitlement?.signals_used_this_period ?? null;
  const packsUsed       = entitlement?.governance_packs_used_this_period ?? null;
  const packsAllowance  = entitlement?.governance_pack_allowance_monthly
                          ?? activePlan?.governance_pack_allowance_monthly ?? null;
  // F.4.4 tidy-up — Evaluations allowance + usage (4th canonical
  // entitlement metric). Source matches Plan & Usage doctrine: prefer
  // the live entitlement row, fall back to the plan catalogue, allow
  // unlimited (null) when neither defines a cap.
  const evalCap         = entitlement?.eval_cap_monthly
                          ?? activePlan?.eval_cap_monthly ?? null;
  const evalUsed        = entitlement?.evaluations_used_this_period ?? null;
  const periodStarts    = entitlement?.period_starts_at;
  const periodEnds      = entitlement?.period_ends_at;
  const memberRoleLabel = ROLE_LABEL[memberRole] || (memberRole || '\u2014');
  const memberRoleHelp  = ROLE_DESCRIPTION[memberRole] || '';

  // F.4.4.DOCTRINE-CORRECTION (2026-02-26)
  // ────────────────────────────────────────────────────────────────
  // SURFACE 1 — top strip → plain-language REMAINING allowance.
  // Fractions are removed (operator complaint: "1 / 1" reads as
  // exhausted). Each pill is now self-describing.
  //   • Entitlement absent      → strip is suppressed entirely.
  //   • Cap === null (unlimited) → "Unlimited <noun>"
  //   • Cap === 0               → "No <noun> available"
  //   • Cap > 0                 → "<remaining> <noun(s)> remaining"
  //                               or "<remaining> active role(s) available"
  // Used clamps to 0 to defend against any webhook race overshoot.
  // SOURCE doctrine (C5): emp_plan_entitlements (current enforced
  // reality). Drift vs emp_plan_catalogue is F.4.5's job.
  const remainingNum = (used, cap) => {
    if (cap === null || cap === undefined) return null;
    const u = (used === null || used === undefined) ? 0 : Number(used);
    return Math.max(0, Number(cap) - u);
  };
  const plur = (n, sing, plural) => (Number(n) === 1 ? sing : plural);

  const rolesRemaining = remainingNum(activeRolesUsed, activeRolesCap);
  const evalsRemaining = remainingNum(evalUsed,        evalCap);
  const packsRemaining = remainingNum(packsUsed,       packsAllowance);
  const sigsRemaining  = remainingNum(signalsUsed,     signalsAllowance);

  const rolesPillText = activeRolesCap === null
    ? 'Unlimited active roles'
    : `${rolesRemaining} ${plur(rolesRemaining, 'active role', 'active roles')} available`;
  const evalsPillText = evalCap === null
    ? 'Unlimited evaluations'
    : `${evalsRemaining} ${plur(evalsRemaining, 'evaluation', 'evaluations')} remaining`;
  const packsPillText = packsAllowance === null
    ? 'Unlimited governance packs'
    : `${packsRemaining} ${plur(packsRemaining, 'governance pack', 'governance packs')} remaining`;
  const sigsPillText  = signalsAllowance === null
    ? 'Unlimited signals'
    : `${sigsRemaining} ${plur(sigsRemaining, 'signal', 'signals')} remaining this month`;

  return (
    <Card
      className="border-ndor-edge shadow-sm"
      data-testid="employer-dashboard-panel"
    >
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-ndor-ink-muted" />
          Operational summary
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="h-7 w-7 p-0"
          aria-label="Refresh dashboard"
          data-testid="employer-dashboard-refresh"
        >
          {loading
            ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : <RefreshCw className="h-3.5 w-3.5" />}
        </Button>
      </CardHeader>

      <CardContent className="space-y-5">
        {/* ── Identity block (Prompt 3 Part B) ─────────────────── */}
        {memberRole && (
          <div
            className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5"
            data-testid="employer-dashboard-identity"
          >
            <div className="flex items-center gap-2 text-xs">
              <span className="text-ndor-ink-muted uppercase tracking-wider text-[10px]">
                You are
              </span>
              <Badge variant="outline" data-testid="employer-dashboard-my-role">
                {memberRoleLabel}
              </Badge>
              <Link
                to="/employer/help/permissions"
                className="ml-auto text-[11px] text-ndor-ink-muted underline-offset-2 hover:underline"
                data-testid="employer-dashboard-permissions-link"
              >
                Roles &amp; permissions
              </Link>
            </div>
            {memberRoleHelp && (
              <p className="mt-1.5 text-[11px] text-ndor-ink-secondary leading-relaxed">
                {memberRoleHelp}
              </p>
            )}
          </div>
        )}

        {/* ── Counters grid ─────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Stat
            testId="employer-dashboard-active-roles"
            icon={<Briefcase className="h-3.5 w-3.5" />}
            label="Active roles"
            value={counts.activeRoles}
            href="/employer/roles"
          />
          <Stat
            testId="employer-dashboard-open-reviews"
            icon={<ClipboardList className="h-3.5 w-3.5" />}
            label="Open reviews"
            value={counts.accountOpenReviews}
            hint="Across all reviewers on this account."
          />
          <Stat
            testId="employer-dashboard-active-reviewers"
            icon={<UserCheck className="h-3.5 w-3.5" />}
            label="Active reviewers"
            value={counts.activeReviewers}
            href="/employer/reviewers"
          />
          <Stat
            testId="employer-dashboard-pending-invites"
            icon={<MailPlus className="h-3.5 w-3.5" />}
            label="Pending invites"
            value={counts.pendingInvites}
            href="/employer/members"
          />
        </div>

        {/* ── F.4.4.DOCTRINE-CORRECTION (2026-02-26) operational
            summary strip. Plain-language remaining allowance.
            Layout: two visually distinct ROWS — identity row on top,
            labelled allowance pill grid below. The grid gives each
            pill its own visual cell so long copy (e.g. "Unlimited
            governance packs") never crashes into the next pill.
            Wraps to 2 cols on tablet, 1 col on mobile.
            Source: emp_plan_entitlements (current enforced reality).
            Drift vs emp_plan_catalogue is F.4.5's job. */}
        <div
          className="space-y-3"
          data-testid="employer-dashboard-plan-strip"
        >
          {/* Row 1 — identity */}
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant="outline"
              className="font-normal"
              data-testid="employer-dashboard-plan-tier"
            >
              <Tag className="h-3 w-3 mr-1" />
              {tierLabel} plan
            </Badge>
            <Badge
              variant={activationVariant}
              data-testid="employer-dashboard-activation-badge"
            >
              {activationCopy}
            </Badge>
          </div>

          {/* Row 2 — labelled allowance grid (plain-language remaining). */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-ndor-ink-muted mb-2">
              Remaining this period
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
              <div
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink"
                data-testid="employer-dashboard-roles-left"
              >
                {rolesPillText}
              </div>
              <div
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink"
                data-testid="employer-dashboard-evaluations-left"
              >
                {evalsPillText}
              </div>
              <div
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink"
                data-testid="employer-dashboard-packs-left"
              >
                {packsPillText}
              </div>
              <div
                className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2 text-xs text-ndor-ink"
                data-testid="employer-dashboard-signals-left"
              >
                {sigsPillText}
              </div>
            </div>
          </div>
        </div>

        {/* ── Plan & Usage panel (Prompt 3 Part D — Option I) ────
            Surfaces live usage when an entitlement row exists. When
            it doesn't (the Phase 0.4 default), renders honestly as
            "—" so we never invent metering data. */}
        <div
          className="rounded-md border border-ndor-edge bg-ndor-surface-alt/30 px-3 py-3 space-y-2"
          data-testid="employer-dashboard-usage"
        >
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium text-ndor-ink uppercase tracking-wider">
              Plan &amp; usage
            </h4>
            {entitlement ? (
              periodStarts && periodEnds && (
                <span className="text-[10px] text-ndor-ink-muted">
                  Period: {new Date(periodStarts).toLocaleDateString()} → {new Date(periodEnds).toLocaleDateString()}
                </span>
              )
            ) : (
              // RC 1.61 (operator-approved 2026-05-29): replaced the
              // stale "Free Beta · usage metering inactive" copy with
              // a neutral "awaiting plan activation" line. This branch
              // only renders when the entitlement row is missing — an
              // edge state with no Stripe context to surface.
              <span className="text-[10px] text-ndor-ink-muted">
                Awaiting plan activation
              </span>
            )}
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
            <div data-testid="employer-dashboard-usage-active-roles">
              <dt className="text-[10px] uppercase text-ndor-ink-muted">Roles used</dt>
              <dd className="font-mono tabular-nums text-ndor-ink">
                {fmtUsage(activeRolesUsed, activeRolesCap)}
              </dd>
            </div>
            <div data-testid="employer-dashboard-usage-evaluations">
              <dt className="text-[10px] uppercase text-ndor-ink-muted">Evaluations used</dt>
              <dd className="font-mono tabular-nums text-ndor-ink">
                {fmtUsage(evalUsed, evalCap)}
              </dd>
            </div>
            <div data-testid="employer-dashboard-usage-packs">
              <dt className="text-[10px] uppercase text-ndor-ink-muted">Governance packs used</dt>
              <dd className="font-mono tabular-nums text-ndor-ink">
                {fmtUsage(packsUsed, packsAllowance)}
              </dd>
            </div>
            <div data-testid="employer-dashboard-usage-signals">
              <dt className="text-[10px] uppercase text-ndor-ink-muted">Signals used this month</dt>
              <dd className="font-mono tabular-nums text-ndor-ink">
                {fmtUsage(signalsUsed, signalsAllowance)}
              </dd>
            </div>
          </dl>
        </div>

        {/* ── Operationally honest plan footnote ─────────────────── */}
        {isPlaceholder && (
          <div
            className="flex items-start gap-2 rounded-md border border-ndor-edge bg-ndor-surface-alt px-3 py-2 text-xs text-ndor-ink-secondary"
            data-testid="employer-dashboard-placeholder-note"
          >
            <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-ndor-ink-muted" />
            <span>
              {PLACEHOLDER_PLAN_COPY} Quotas above reflect the placeholder
              tier and may change before launch.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function Stat({ testId, icon, label, value, hint, href }) {
  const body = (
    <div
      className="rounded-md border border-ndor-edge bg-ndor-surface-alt/40 px-3 py-2.5 h-full"
      data-testid={testId}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-ndor-ink-muted">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1.5 text-xl font-mono tabular-nums text-ndor-ink">
        {value === null || value === undefined ? '\u2014' : value}
      </div>
      {hint && (
        <div className="mt-1 text-[10px] leading-snug text-ndor-ink-muted">
          {hint}
        </div>
      )}
      {href && (
        <div className="mt-1 text-[10px] text-ndor-ink-muted flex items-center gap-0.5">
          <span>Open</span>
          <ArrowRight className="h-2.5 w-2.5" />
        </div>
      )}
    </div>
  );
  if (href) {
    return (
      <Link
        to={href}
        className="block hover:bg-ndor-surface-alt/60 rounded-md transition-colors"
        data-testid={`${testId}-link`}
      >
        {body}
      </Link>
    );
  }
  return body;
}
